import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import { OnlineThemeType } from '../../themes/online';
import chevronDownIcon from '../../assets/chevron-down-icon.svg';
import { IAppBarItem } from './AppBar.models';

export interface IProfileProps {
  className?: string;
  userFullName: string;
  profileMenuItems: IAppBarItem[];
  subTitle?: string;
  onProfileMenuItemClick: (name: string) => void;
}

export interface IProfilePropsWithTheme extends IProfileProps {
  theme: OnlineThemeType;
}

const StyledProfile = ({ theme }: IProfilePropsWithTheme) => {
  return css`
    margin: auto 0;
    display: flex;
    justify-content: center;
    align-items: center;
    padding-inline: 16px 32px;

    > .drop-shadow {
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      background-color: rgba(0, 0, 0, 0.05);
    }
    > .profile-container {
      display: flex;
      flex-direction: row;
      justify-content: center;
      align-items: start;
      padding: 0px;
      gap: 16px;
      cursor: pointer;
      position: relative;

      .profile-info {
        width: max-content;
        max-width: 160px;
      }

      > .profile-pic {
        width: 24px;
        height: 24px;
        border-radius: 6px;
      }

      > img {
        margin-top: 7px;
      }
      .fullname {
        font-style: normal;
        font-weight: 700;
        font-size: 16px;
        line-height: 20px;
        color: ${theme.colors.primaryBackground};
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;

        @media ${theme.device.max.laptopM} {
          display: none;
        }
      }
      .subtitle {
        font-style: normal;
        font-weight: 700;
        font-size: 12px;
        color: ${theme.colors.placeholder2};
      }

      > .profile-menu {
        position: fixed;
        background-color: ${theme.colors.white};
        min-width: 196px;
        z-index: 2;
        box-shadow: 0px 8px 28px -6px rgba(24, 39, 75, 0.12), 0px 18px 88px -4px rgba(24, 39, 75, 0.14);
        border-radius: 8px;
        top: 60px;
        right: 6px;
        display: flex;
        flex-direction: column;
        padding: 16px 8px;
        gap: 8px;

        > .profile-menu-item {
          display: flex;
          flex-direction: row;
          align-items: center;
          padding: 12px 10px;
          gap: 10px;
          height: 45px;
          font-style: normal;
          font-weight: 500;
          font-size: 16px;
          line-height: 21px;
          color: ${theme.colors.placeholder};
          border-radius: 4px;

          &:hover {
            background-color: ${theme.colors.grayWhite};
          }
        }
      }
    }
  `;
};

export const Profile = styled((props: IProfileProps) => {
  const [isProfileOpen, setIsProfileOpen] = useState<boolean>(false);
  const toggleProfileMenu = () => setIsProfileOpen(!isProfileOpen);

  const onItemClick = (name: string) => {
    setIsProfileOpen(false);
    props.onProfileMenuItemClick(name);
  };

  return (
    <div className={props.className}>
      {isProfileOpen && <div onClick={toggleProfileMenu} className="drop-shadow"></div>}
      <div className="profile-container" onClick={toggleProfileMenu} data-at="appbar-profile-dropdown">
        <div className="profile-info">
          <div className="fullname">{props.userFullName}</div>
          {props.subTitle && <div className="subtitle">{props.subTitle}</div>}
        </div>
        {props.profileMenuItems.length ? (
          <>
            <img src={chevronDownIcon} />
            {isProfileOpen && (
              <div className="profile-menu">
                {props.profileMenuItems.map((item) => (
                  <div
                    key={item.name}
                    className="profile-menu-item"
                    onClick={() => onItemClick(item.name)}
                    data-at={`appbar-profile-dropdown-${item.name}`}
                  >
                    {item.icon}
                    <span>{item.title}</span>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          ''
        )}
      </div>
    </div>
  );
})`
  ${StyledProfile}
`;
